import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { 
  Search, 
  Loader2, 
  Package, 
  ClipboardList, 
  Factory,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  trackingApiService,
  RawTrackingRecord,
  HeatDetails,
  WorkOrderDetails,
  BoxDetails
} from '../services/trackingApi';
import { formatDate } from '../utils/dateUtils';

type SearchType = 'heatNumber' | 'boxNumber' | 'workOrderNumber';

const TrackingPage: React.FC = () => {
  const [searchType, setSearchType] = useState<SearchType>('heatNumber');
  const [searchValue, setSearchValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [rawResults, setRawResults] = useState<RawTrackingRecord[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Memoized function to process raw results into distinct sections
  const processedResults = useMemo(() => {
    // Ensure rawResults is an array
    if (!rawResults || !Array.isArray(rawResults) || rawResults.length === 0) {
      return { heatDetails: [], workOrderDetails: [], boxDetails: [] };
    }

    const heatMap = new Map<string, HeatDetails>();
    const workOrderMap = new Map<string, WorkOrderDetails>();
    const boxMap = new Map<string, BoxDetails>();

    rawResults.forEach(record => {
      // Process for Heat Details
      if (!heatMap.has(record.heatNo)) {
        heatMap.set(record.heatNo, {
          heatNo: record.heatNo,
          grnInfo: {
            grnId: record.grnId,
            grnNumber: record.grnNumber,
            grnReceivedDate: record.grnReceivedDate,
            grnReceivedQuantity: record.grnReceivedQuantity,
            grnQualityCheck: record.grnQualityCheck,
            supplierHeatNo: record.supplierHeatNo,
            tcNo: record.tcNo,
            tcDate: record.tcDate,
            rawItemName: record.rawItemName,
            rawItemSku: record.rawItemSku,
          },
          associatedWorkOrders: [],
          associatedBoxes: [],
        });
      }
      const currentHeat = heatMap.get(record.heatNo)!;

      // Add/Update Work Order in Heat Details
      let heatAssociatedWorkOrder = currentHeat.associatedWorkOrders.find(
        (wo) => wo.workOrderNumber === record.workOrderNumber
      );
      if (!heatAssociatedWorkOrder) {
        heatAssociatedWorkOrder = {
          workOrderId: record.workOrderId,
          workOrderNumber: record.workOrderNumber,
          workOrderStatus: record.workOrderStatus,
          completedQuantity: record.completedQuantity,
          rejectQuantity: record.rejectQuantity,
          workOrderCreatedDate: record.workOrderCreatedDate,
          operations: [],
        };
        currentHeat.associatedWorkOrders.push(heatAssociatedWorkOrder);
      }
      // Add operation to this specific work order if it's new
      const existingOperationInHeatWO = heatAssociatedWorkOrder.operations.find(
        (op) => op.operationName === record.operationName && op.productionDate === record.productionDate
      );
      if (!existingOperationInHeatWO && record.operationName) {
        heatAssociatedWorkOrder.operations.push({
          operationName: record.operationName,
          productionDate: record.productionDate,
          operationCompletedQuantity: record.operationCompletedQuantity,
          operationRejectQuantity: record.operationRejectQuantity,
        });
      }

      // Add/Update Box in Heat Details
      const heatAssociatedBox = currentHeat.associatedBoxes.find(
        (box) => box.boxNumber === record.boxNumber
      );
      if (!heatAssociatedBox && record.boxNumber) {
        currentHeat.associatedBoxes.push({
          boxNumber: record.boxNumber,
          boxQuantity: record.boxQuantity,
          boxStatus: record.boxStatus,
          packingHeaderNo: record.packingHeaderNo,
          packingDate: record.packingDate,
          packingStatus: record.packingStatus,
          isInvoiced: record.isInvoiced,
          invoiceNumber: record.invoiceNumber,
          invoiceDate: record.invoiceDate,
          salesOrderNumber: record.salesOrderNumber,
          customerName: record.customerName,
        });
      }

      // Process for Work Order Details
      if (!workOrderMap.has(record.workOrderNumber)) {
        workOrderMap.set(record.workOrderNumber, {
          workOrderNumber: record.workOrderNumber,
          workOrderId: record.workOrderId,
          workOrderStatus: record.workOrderStatus,
          completedQuantity: record.completedQuantity,
          rejectQuantity: record.rejectQuantity,
          workOrderCreatedDate: record.workOrderCreatedDate,
          endgoodName: record.endgoodName,
          endgoodSku: record.endgoodSku,
          associatedHeatNumbers: [],
          operations: [],
          associatedBoxes: [],
        });
      }
      const currentWorkOrder = workOrderMap.get(record.workOrderNumber)!;

      // Add/Update Heat Number in Work Order Details
      const woAssociatedHeat = currentWorkOrder.associatedHeatNumbers.find(
        (h) => h.heatNo === record.heatNo
      );
      if (!woAssociatedHeat) {
        currentWorkOrder.associatedHeatNumbers.push({
          heatNo: record.heatNo,
          materialIssueDate: record.issueDate,
          issueQuantity: record.issueQuantity,
        });
      }

      // Add unique operations to Work Order details if new
      const woExistingOperation = currentWorkOrder.operations.find(
        (op) => op.operationName === record.operationName && op.productionDate === record.productionDate
      );
      if (!woExistingOperation && record.operationName) {
        currentWorkOrder.operations.push({
          operationName: record.operationName,
          productionDate: record.productionDate,
          operationCompletedQuantity: record.operationCompletedQuantity,
          operationRejectQuantity: record.operationRejectQuantity,
        });
      }

      // Add/Update Box in Work Order Details
      const woAssociatedBox = currentWorkOrder.associatedBoxes.find(
        (box) => box.boxNumber === record.boxNumber
      );
      if (!woAssociatedBox && record.boxNumber) {
        currentWorkOrder.associatedBoxes.push({
          boxNumber: record.boxNumber,
          boxQuantity: record.boxQuantity,
          boxStatus: record.boxStatus,
          packingHeaderNo: record.packingHeaderNo,
          packingDate: record.packingDate,
          isInvoiced: record.isInvoiced,
          invoiceNumber: record.invoiceNumber,
          invoiceDate: record.invoiceDate,
        });
      }

      // Process for Box Details
      if (!boxMap.has(record.boxNumber) && record.boxNumber) {
        boxMap.set(record.boxNumber, {
          boxNumber: record.boxNumber,
          boxQuantity: record.boxQuantity,
          boxStatus: record.boxStatus,
          packingHeaderNo: record.packingHeaderNo,
          packingDate: record.packingDate,
          packingStatus: record.packingStatus,
          isInvoiced: record.isInvoiced,
          invoiceNumber: record.invoiceNumber,
          invoiceDate: record.invoiceDate,
          salesOrderNumber: record.salesOrderNumber,
          customerName: record.customerName,
          associatedHeatNumbers: [],
          associatedWorkOrders: [],
        });
      }
      const currentBox = boxMap.get(record.boxNumber);
      if (currentBox) {
        // Add/Update Heat Number in Box Details
        const boxAssociatedHeat = currentBox.associatedHeatNumbers.find(
          (h) => h.heatNo === record.heatNo
        );
        if (!boxAssociatedHeat) {
          currentBox.associatedHeatNumbers.push({
            heatNo: record.heatNo,
            rawItemName: record.rawItemName,
            materialIssueDate: record.issueDate,
          });
        }

        // Add/Update Work Order in Box Details
        const boxAssociatedWorkOrder = currentBox.associatedWorkOrders.find(
          (wo) => wo.workOrderNumber === record.workOrderNumber
        );
        if (!boxAssociatedWorkOrder) {
          currentBox.associatedWorkOrders.push({
            workOrderNumber: record.workOrderNumber,
            endgoodName: record.endgoodName,
            completedQuantity: record.completedQuantity,
            rejectQuantity: record.rejectQuantity,
          });
        }
      }
    });

    return {
      heatDetails: Array.from(heatMap.values()),
      workOrderDetails: Array.from(workOrderMap.values()),
      boxDetails: Array.from(boxMap.values()),
    };
  }, [rawResults]);

  const handleSearch = useCallback(async () => {
    if (!searchValue.trim()) {
      toast.error('Please enter a search value');
      return;
    }

    setError(null);
    setLoading(true);
    setRawResults([]);

    try {
      let data: RawTrackingRecord[] = [];
      if (searchType === 'heatNumber') {
        data = await trackingApiService.getTrackingByHeatNumber(searchValue.trim());
      } else if (searchType === 'boxNumber') {
        data = await trackingApiService.getTrackingByBoxNumber(searchValue.trim());
      } else {
        data = await trackingApiService.getTrackingByWorkOrderNumber(searchValue.trim());
      }
      // Ensure data is always an array
      setRawResults(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'An unknown error occurred');
      setRawResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchType, searchValue]);

  const handleClear = () => {
    setSearchValue('');
    setRawResults([]);
    setError(null);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Tracking</h1>
        <p className="text-gray-600 mt-1">Track heat numbers, boxes, and work orders</p>
      </div>

      {/* Search Section */}
      <Card className="border-primary-200 shadow-md">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <CardTitle className="text-lg text-primary-800">Search</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Search Type Selection */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-3 block">Search By</Label>
              <div className="flex gap-4">
                <Button
                  variant={searchType === 'heatNumber' ? 'default' : 'outline'}
                  onClick={() => {
                    setSearchType('heatNumber');
                    handleClear();
                  }}
                  className={searchType === 'heatNumber' ? 'bg-primary-600 hover:bg-primary-700' : ''}
                >
                  Heat Number
                </Button>
                <Button
                  variant={searchType === 'boxNumber' ? 'default' : 'outline'}
                  onClick={() => {
                    setSearchType('boxNumber');
                    handleClear();
                  }}
                  className={searchType === 'boxNumber' ? 'bg-primary-600 hover:bg-primary-700' : ''}
                >
                  Box Number
                </Button>
                <Button
                  variant={searchType === 'workOrderNumber' ? 'default' : 'outline'}
                  onClick={() => {
                    setSearchType('workOrderNumber');
                    handleClear();
                  }}
                  className={searchType === 'workOrderNumber' ? 'bg-primary-600 hover:bg-primary-700' : ''}
                >
                  Work Order Number
                </Button>
              </div>
            </div>

            {/* Search Input */}
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="searchValue" className="text-sm font-medium text-gray-700">
                  {searchType === 'heatNumber' && 'Heat Number'}
                  {searchType === 'boxNumber' && 'Box Number'}
                  {searchType === 'workOrderNumber' && 'Work Order Number'}
                </Label>
                <Input
                  id="searchValue"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder={
                    searchType === 'heatNumber' ? 'Enter heat number (e.g., MS-IA12-01)' :
                    searchType === 'boxNumber' ? 'Enter box number (e.g., B-27783-003)' :
                    'Enter work order number (e.g., WO-2024-001)'
                  }
                  className="mt-1"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  disabled={loading || !searchValue.trim()}
                  className="bg-primary-600 hover:bg-primary-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Search
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {loading ? (
        <Card>
          <CardContent className="p-12">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              <span className="ml-2 text-lg">Loading tracking data...</span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {error && (
            <Card className="border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center text-red-600">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span>{error}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {!loading && rawResults.length === 0 && searchValue.trim() && !error && (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500 text-lg">No results found</p>
                  <p className="text-gray-400 text-sm mt-2">Try searching with a different value</p>
                </div>
              </CardContent>
            </Card>
          )}

          {rawResults.length > 0 && (
            <div className="mt-8 space-y-8">
              <h2 className="text-2xl font-bold">Search Results</h2>

              {/* Heat Number Details Section */}
              {processedResults.heatDetails.length > 0 && (
                <section>
                  <h3 className="text-xl font-semibold mb-4">Heat Number Details</h3>
                  {processedResults.heatDetails.map((heat) => (
                    <Card key={heat.heatNo} className="mb-4 border-primary-200 shadow-md">
                      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
                        <CardTitle className="text-lg text-primary-800">Heat Number: {heat.heatNo}</CardTitle>
                        <CardDescription>Details for heat number {heat.heatNo}</CardDescription>
                      </CardHeader>
                      <CardContent className="p-6 space-y-4">
                        <h4 className="font-semibold text-lg">GRN Information</h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>GRN Number</TableHead>
                              <TableHead>Received Date</TableHead>
                              <TableHead>Issue Quantity</TableHead>
                              <TableHead>Supplier Heat No</TableHead>
                              <TableHead>TC Number</TableHead>
                              <TableHead>TC Date</TableHead>
                              <TableHead>Quality Check</TableHead>
                              <TableHead>Raw Item</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <TableRow>
                              <TableCell>{heat.grnInfo.grnNumber}</TableCell>
                              <TableCell>{formatDate(heat.grnInfo.grnReceivedDate)}</TableCell>
                              <TableCell>{formatNumber(heat.grnInfo.grnReceivedQuantity)}</TableCell>
                              <TableCell>{heat.grnInfo.supplierHeatNo}</TableCell>
                              <TableCell>{heat.grnInfo.tcNo}</TableCell>
                              <TableCell>{heat.grnInfo.tcDate ? formatDate(heat.grnInfo.tcDate) : 'N/A'}</TableCell>
                              <TableCell>{heat.grnInfo.grnQualityCheck || 'N/A'}</TableCell>
                              <TableCell>{heat.grnInfo.rawItemName} ({heat.grnInfo.rawItemSku})</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </section>
              )}

              {/* Distinct Work Orders Section */}
              {processedResults.workOrderDetails.length > 0 && (
                <section>
                  <h3 className="text-xl font-semibold mb-4">Work Orders</h3>
                  {processedResults.workOrderDetails.map((wo) => (
                    <Card key={wo.workOrderNumber} className="mb-4 border-primary-200 shadow-md">
                      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
                        <CardTitle className="text-lg text-primary-800">Work Order: {wo.workOrderNumber}</CardTitle>
                        <CardDescription>End Good: {wo.endgoodName} ({wo.endgoodSku})</CardDescription>
                      </CardHeader>
                      <CardContent className="p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Work Order Number</Label>
                            <p className="text-sm font-semibold text-gray-900">{wo.workOrderNumber}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Status</Label>
                            <p className="text-sm font-semibold text-gray-900">{wo.workOrderStatus}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">End Good</Label>
                            <p className="text-sm font-semibold text-gray-900">{wo.endgoodName}</p>
                            <p className="text-xs text-gray-500">SKU: {wo.endgoodSku}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Completed Quantity</Label>
                            <p className="text-sm font-semibold text-gray-900">{formatNumber(wo.completedQuantity)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Reject Quantity</Label>
                            <p className="text-sm font-semibold text-gray-900">{formatNumber(wo.rejectQuantity)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Created Date</Label>
                            <p className="text-sm font-semibold text-gray-900">{formatDate(wo.workOrderCreatedDate)}</p>
                          </div>
                        </div>

                        {/* Operations Table */}
                        <div className="mt-6">
                          <h4 className="font-semibold text-lg mb-4">Operations</h4>
                          {wo.operations.length > 0 ? (
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Operation Name</TableHead>
                                    <TableHead>Production Date</TableHead>
                                    <TableHead>Completed Quantity</TableHead>
                                    <TableHead>Reject Quantity</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {[...wo.operations]
                                    .sort((a, b) => {
                                      const dateA = new Date(a.productionDate).getTime();
                                      const dateB = new Date(b.productionDate).getTime();
                                      return dateA - dateB; // Ascending order
                                    })
                                    .map((op, opIndex) => (
                                    <TableRow key={opIndex}>
                                      <TableCell>{op.operationName}</TableCell>
                                      <TableCell>{formatDate(op.productionDate)}</TableCell>
                                      <TableCell>{formatNumber(op.operationCompletedQuantity)}</TableCell>
                                      <TableCell>{formatNumber(op.operationRejectQuantity)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <p className="text-gray-500">No operations recorded for this work order.</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </section>
              )}

              {/* Distinct Box Numbers Section */}
              {processedResults.boxDetails.length > 0 && (
                <section>
                  <h3 className="text-xl font-semibold mb-4">Box Numbers</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Box Number</TableHead>
                          <TableHead>Packing Header</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Packing Date</TableHead>
                          <TableHead>Packing Status</TableHead>
                          <TableHead>Invoiced</TableHead>
                          <TableHead>Invoice Number</TableHead>
                          <TableHead>Invoice Date</TableHead>
                          <TableHead>Sales Order</TableHead>
                          <TableHead>Customer</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {processedResults.boxDetails.map((box, boxIndex) => (
                          <TableRow key={boxIndex}>
                            <TableCell className="font-medium">{box.boxNumber}</TableCell>
                            <TableCell>{box.packingHeaderNo}</TableCell>
                            <TableCell>{formatNumber(box.boxQuantity)}</TableCell>
                            <TableCell>{box.boxStatus}</TableCell>
                            <TableCell>{formatDate(box.packingDate)}</TableCell>
                            <TableCell>{box.packingStatus}</TableCell>
                            <TableCell>
                              {box.isInvoiced ? (
                                <Badge className="bg-blue-600 text-white">Yes</Badge>
                              ) : (
                                <Badge variant="outline">No</Badge>
                              )}
                            </TableCell>
                            <TableCell>{box.invoiceNumber || 'N/A'}</TableCell>
                            <TableCell>{box.invoiceDate ? formatDate(box.invoiceDate) : 'N/A'}</TableCell>
                            <TableCell>{box.salesOrderNumber}</TableCell>
                            <TableCell>{box.customerName}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </section>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrackingPage;
